import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { money } from "@/lib/format";
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const CARDS = [
  ["collected_cents", "Total collected"], ["principal_cents", "Principal collected"], ["interest_cents", "Interest earned"],
  ["fees_cents", "Fees collected"], ["late_fees_assessed_cents", "Late fees assessed"], ["fees_waived_cents", "Fees waived"],
  ["card_cents", "Card collected"], ["bank_cents", "Bank (ACH) collected"], ["manual_cents", "Manual collected"],
  ["new_loans", "New loans originated"], ["outstanding_principal_cents", "Outstanding principal (now)"],
  ["late_loans_now", "Late loans (now)"], ["past_due_now_cents", "Past due (now)"],
];

const monthLabel = (m) => new Date(`${m}-15T12:00:00`).toLocaleDateString("en-US", { month: "short", year: "2-digit" });

export const SummaryCards = ({ s }) => (
  <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5" data-testid="report-summary">
    {CARDS.map(([k, label], i) => (
      <div key={k} className="fade-up rounded-lg border bg-card p-4" style={{ animationDelay: `${i * 40}ms` }} data-testid={`summary-${k}`}>
        <div className="eyebrow text-[10px]">{label}</div>
        <div className="num mt-2 font-display text-xl font-semibold sm:text-2xl" data-testid={`summary-${k}-value`}>
          {k === "new_loans" ? money(s.new_loans_cents) : k === "late_loans_now" ? s.late_loans_now : money(s[k])}
        </div>
        {k === "new_loans" && <div className="text-xs text-muted-foreground">{s.new_loans_count} loan{s.new_loans_count === 1 ? "" : "s"}</div>}
        {k === "collected_cents" && <div className="text-xs text-muted-foreground">{s.payments_count} payments{s.excess_cents ? ` · incl. ${money(s.excess_cents)} overpayment` : ""}</div>}
      </div>
    ))}
  </div>
);

export const CollectionsChart = ({ months }) => {
  const data = months.map((m) => ({ month: monthLabel(m.month), Principal: m.principal_cents / 100, Interest: m.interest_cents / 100, Fees: m.fees_cents / 100 }));
  return (
    <div className="mt-6 rounded-lg border bg-card p-5" data-testid="collections-chart">
      <h3 className="mb-4 text-lg font-semibold">Monthly collections</h3>
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ left: 8, right: 8 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
            <XAxis dataKey="month" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `$${v >= 1000 ? `${Math.round(v / 1000)}k` : v}`} width={56} />
            <Tooltip formatter={(v) => money(Math.round(v * 100))} />
            <Legend />
            <Bar dataKey="Principal" stackId="a" fill="hsl(var(--primary))" />
            <Bar dataKey="Interest" stackId="a" fill="hsl(var(--gold))" />
            <Bar dataKey="Fees" stackId="a" fill="hsl(var(--muted-foreground))" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const cell = (row, [k, , isMoney]) => (k === "month" ? monthLabel(row.month) : isMoney ? money(row[k]) : row[k]);

export const MonthlyTable = ({ rep }) => {
  const cols = rep.columns.monthly;
  const totals = Object.fromEntries(cols.map(([k]) => [k, rep.months.reduce((s, m) => s + (typeof m[k] === "number" ? m[k] : 0), 0)]));
  return (
    <div className="mt-6 overflow-x-auto rounded-lg border bg-card" data-testid="monthly-table">
      <Table>
        <TableHeader><TableRow>{cols.map(([k, l]) => <TableHead key={k} className="whitespace-nowrap text-right first:text-left">{l}</TableHead>)}</TableRow></TableHeader>
        <TableBody>
          {rep.months.map((m) => (
            <TableRow key={m.month} data-testid={`monthly-row-${m.month}`}>
              {cols.map((c) => <TableCell key={c[0]} className="num whitespace-nowrap text-right first:text-left" data-testid={`monthly-${m.month}-${c[0]}`}>{cell(m, c)}</TableCell>)}
            </TableRow>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow data-testid="monthly-totals">
            {cols.map(([k, , isMoney], i) => (
              <TableCell key={k} className="num whitespace-nowrap text-right font-semibold first:text-left">
                {i === 0 ? "Total" : ["late_loans", "past_due_cents"].includes(k) ? "—" : isMoney ? money(totals[k]) : totals[k]}
              </TableCell>
            ))}
          </TableRow>
        </TableFooter>
      </Table>
    </div>
  );
};
